import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from '../api/Axios';

const DragDropUpload = ({ 
  documentType, 
  title, 
  onDocumentUploaded, 
  onDocumentUploadFailed, 
  uploadedFilenames,
  isUploading,
  onUploadStart,
  hasError,
  uploadEndpoint = '/accounts/student-docs/',
  compact = false,
  customUploadHandler = null // Optional: custom upload function
}) => {
  const [localUploading, setLocalUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  
  // Get user id from localStorage
  const userId = localStorage.getItem("user_id");

  // Map document types to display names
  const getDocumentTypeDisplayName = useCallback((docType) => {
    const typeMap = {
      'id_passport': 'Photo of Identity/Passport Document',
      'student_card': 'Photo of Student Card',
      'acceptance_letter': 'Photo of Acceptance Letter',
      'passport_photo': 'Passport Size Photo',
      'proof_of_address': 'Proof of Address',
      'qualification_doc': 'Qualification Certificate/Transcript'
    };
    return typeMap[docType] || 'Document';
  }, []);

  const uploadFile = useCallback(async (file) => {
    if (!userId) {
      setUploadError("User not logged in.");
      return;
    }

    // Validate file size (12MB limit)
    const maxSizeInBytes = 12 * 1024 * 1024; // 12MB
    if (file.size > maxSizeInBytes) {
      const errorMsg = `File size exceeds 12MB limit. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB.`;
      setUploadError(errorMsg);
      if (onDocumentUploadFailed) {
        onDocumentUploadFailed(documentType);
      }
      return;
    }

    // Validate file type (common document/image types)
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp',
      'application/pdf',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      const errorMsg = "Please upload a valid document (PDF, Word, Excel, Image, or Text file).";
      setUploadError(errorMsg);
      if (onDocumentUploadFailed) {
        onDocumentUploadFailed(documentType);
      }
      return;
    }

    try {
      setLocalUploading(true);
      setUploadError('');
      
      if (onUploadStart) {
        onUploadStart(documentType);
      }

      const formData = new FormData();
      formData.append('document', file);
      formData.append('user', userId);
      // Map document types to specific doc_type values for database
      if (documentType === 'qualification_doc') {
        formData.append('doc_type', '1');
      } else if (documentType === 'honours_doc') {
        formData.append('doc_type', '2');
      } else if (documentType === 'masters_doc') {
        formData.append('doc_type', '3');
      } else if (documentType === 'doctorate_doc') {
        formData.append('doc_type', '4');
      } else if (documentType === 'id_passport' && uploadEndpoint.includes('tutor-docs')) {
        // For tutor identification documents, set doc_type as "Identity Verification"
        formData.append('doc_type', 'Identity Verification');
      } else if (documentType === 'proof_of_account' && uploadEndpoint.includes('tutor-docs')) {
        // For tutor banking documents, set doc_type as "Proof of Bank Account"
        formData.append('doc_type', 'Proof of Bank Account');
      } else {
        formData.append('doc_type', getDocumentTypeDisplayName(documentType));
      }

      const response = await axios.post(uploadEndpoint, formData);

      if (response.status === 201) {
        if (onDocumentUploaded) {
          onDocumentUploaded(documentType, file.name);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      let errorMessage = "Upload failed. Please try again.";
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.document) {
          errorMessage = Array.isArray(error.response.data.document) 
            ? error.response.data.document[0] 
            : error.response.data.document;
        }
      }
      
      setUploadError(errorMessage);
      if (onDocumentUploadFailed) {
        onDocumentUploadFailed(documentType);
      }
    } finally {
      setLocalUploading(false);
    }
  }, [userId, documentType, onDocumentUploaded, onDocumentUploadFailed, onUploadStart, getDocumentTypeDisplayName]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]; // Only handle the first file
      
      // Use custom handler if provided, otherwise use default upload
      if (customUploadHandler) {
        customUploadHandler(file);
      } else {
        uploadFile(file);
      }
    }
  }, [uploadFile, customUploadHandler]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false, // Only allow single file
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt']
    }
  });

  // Get upload state
  const uploading = localUploading || isUploading;
  const uploaded = uploadedFilenames?.has(documentType) || false;
  const filenames = uploadedFilenames?.get(documentType) || [];

  // Get card state for styling
  const getCardClass = () => {
    if (hasError) return 'border-danger';
    if (uploaded) return 'border-success';
    if (uploading) return 'border-warning';
    if (isDragActive) return 'border-primary';
    return 'border-secondary';
  };

  return (
    <div className={compact ? "col-12" : "col-md-6 mb-3"}>
        <div 
        {...getRootProps()}
        className={`card ${compact ? '' : 'h-100'} ${getCardClass()}`}
        style={{ 
          cursor: uploading ? 'not-allowed' : 'pointer',
          backgroundColor: isDragActive ? '#f8f9fa' : 'white',
          transition: 'all 0.3s ease',
          border: '2px dashed',
          borderRadius: '8px',
          ...(compact ? { padding: '0.5rem' } : {}),
          ...(isDragActive && !uploading ? {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
          } : {})
        }}
        onMouseEnter={(e) => {
          if (!uploading) {
            e.target.style.backgroundColor = '#f8f9fa';
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
          }
        }}
        onMouseLeave={(e) => {
          if (!uploading) {
            e.target.style.backgroundColor = isDragActive ? '#f8f9fa' : 'white';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = 'none';
          }
        }}
      >
        <input {...getInputProps()} disabled={uploading} />
        
        {compact ? (
          /* Compact horizontal layout */
          <div className="card-body py-2 px-3 d-flex align-items-center">
            <div className="me-3">
              {uploading ? (
                <div className="spinner-border spinner-border-sm text-warning" role="status">
                  <span className="visually-hidden">Uploading...</span>
                </div>
              ) : uploaded ? (
                <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '1.25rem' }}></i>
              ) : hasError ? (
                <i className="bi bi-x-circle-fill text-danger" style={{ fontSize: '1.25rem' }}></i>
              ) : (
                <i className="bi bi-cloud-upload text-primary" style={{ fontSize: '1.25rem' }}></i>
              )}
            </div>
            <div className="flex-grow-1">
              <div className="fw-bold small">{title}</div>
              {uploaded && filenames.length > 0 ? (
                <small className="text-success">{filenames[0]}</small>
              ) : (
                <small className="text-muted">Click or drag to upload</small>
              )}
            </div>
          </div>
        ) : (
        <div className="card-body d-flex flex-column">
          <div className="text-center flex-grow-1 d-flex flex-column justify-content-center">
            {/* Status Icon */}
            <div className="mb-3">
              {uploading ? (
                <div className="spinner-border text-warning" role="status">
                  <span className="visually-hidden">Uploading...</span>
                </div>
              ) : uploaded ? (
                <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '2rem' }}></i>
              ) : hasError ? (
                <i className="bi bi-x-circle-fill text-danger" style={{ fontSize: '2rem' }}></i>
              ) : (
                <i className="bi bi-cloud-upload text-primary" style={{ fontSize: '2rem' }}></i>
              )}
            </div>

            {/* Title */}
            <div className="fw-bold mb-2">{title}</div>

            {/* Instructions */}
            <div className="text-muted small">
              {uploading ? (
                'Uploading...'
              ) : isDragActive ? (
                'Drop file here'
              ) : (
                'Drag & drop a file here, or click to browse'
              )}
            </div>

            {/* File format info */}
            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
              PDF, Word, Excel, Images (max 12MB)
            </div>
          </div>

          {/* Uploaded filenames */}
          {uploaded && filenames.length > 0 && (
            <div className="mt-3">
              <ul className="list-unstyled mb-0">
                {filenames.map((filename, index) => (
                  <li key={index} className="d-flex align-items-center">
                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                    <small className="text-success">{filename}</small>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Error message */}
          {uploadError && (
            <div className="mt-3">
              <div className="alert alert-danger" style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', marginBottom: 0 }}>
                <small>{uploadError}</small>
              </div>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default DragDropUpload;